import { locales, type Locale } from '../contracts/common.js'
import { en, type MessageKey, type Messages } from './en.js'
import { ptBR } from './pt-BR.js'

export type { Locale, MessageKey, Messages }
export { locales }

export const dictionaries: Record<Locale, Messages> = {
  en,
  'pt-BR': ptBR,
}

export type Vars = Record<string, string | number>

/** Replaces `{placeholder}` tokens. Unknown placeholders are left untouched so
 *  a typo is visible in the UI instead of silently vanishing. */
function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  )
}

export type Translate = (key: MessageKey, vars?: Vars) => string

export function createTranslator(locale: Locale): Translate {
  const dict = dictionaries[locale]
  return (key, vars) => interpolate(dict[key], vars)
}

/** Best-effort match of an Accept-Language header to a locale we actually have. */
export function negotiateLocale(header: string | undefined, fallback: Locale): Locale {
  if (!header) return fallback
  const wanted = header
    .split(',')
    .map((part) => {
      const [tag = '', q = 'q=1'] = part.trim().split(';')
      return { tag: tag.toLowerCase(), q: Number(q.replace('q=', '')) || 0 }
    })
    .sort((a, b) => b.q - a.q)

  for (const { tag } of wanted) {
    const exact = locales.find((l) => l.toLowerCase() === tag)
    if (exact) return exact
    const base = locales.find((l) => l.toLowerCase().split('-')[0] === tag.split('-')[0])
    if (base) return base
  }
  return fallback
}
